import { Component, computed, DestroyRef, effect, inject, OnDestroy, signal } from '@angular/core';
import { NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { concatMap, filter, from, switchMap, take, tap } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { FormValidators } from '@corelabs/angular/forms';
import { ButtonModule } from 'primeng/button';
import { FormInputComponent } from '@shared/forms';
import { UnavailableComponent } from '@shared/components';
import { QuizFacade } from '@app/store/quiz';
import { QuizQuestion } from '@app/store/quiz-questions/models/quiz-question';
import { QuestionSelectionService } from '@app/dialogs';
import { QuizQuestionFacade } from '@app/store/quiz-question';
import { QuestionListComponent } from '../question-list/question-list.component';

@Component({
    selector: 'app-quiz-edit-modal',
    templateUrl: './edit.component.html',
    styleUrls: ['./edit.component.css'],
    imports: [ReactiveFormsModule, TranslateModule, ButtonModule, FormInputComponent, UnavailableComponent, QuestionListComponent],
})
export class EditComponent implements OnDestroy {
    private readonly destroyRef = inject(DestroyRef);
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly quizFacade = inject(QuizFacade);
    private readonly quizQuestionFacade = inject(QuizQuestionFacade);
    private readonly formBuilder = inject(NonNullableFormBuilder);
    private readonly questionSelectionService = inject(QuestionSelectionService);

    private readonly id = this.route.parent?.snapshot.params['id'];
    readonly quiz = this.quizFacade.quiz(this.id);
    readonly loading = this.quizFacade.loading(this.id);
    readonly saving = this.quizFacade.saving;
    readonly error = this.quizFacade.error(this.id);

    readonly form = this.formBuilder.group({
        name: this.formBuilder.control('', [FormValidators.Required, FormValidators.Text]),
    });

    readonly draftQuestions = signal<QuizQuestion[]>([]);
    readonly questionIds = computed(() => this.quiz()?.questionIds ?? []);
    readonly questionsLoading = computed(() => {
        const ids = this.questionIds();
        return ids.some((id) => this.quizQuestionFacade.loading(id)() === true);
    });

    constructor() {
        if (this.id) {
            this.quizFacade.getQuiz(this.id);
        }

        this.quizFacade
            .quizNonNullable$(this.id)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                switchMap(({ questionIds }) => from(questionIds)),
                tap((id) => this.quizQuestionFacade.getQuizQuestion(id)),
                concatMap((id) => {
                    return this.quizQuestionFacade.loadedNonNullable$(id).pipe(
                        take(1),
                        filter((loaded) => loaded === true),
                        switchMap(() => this.quizQuestionFacade.quizQuestionNonNullable$(id)),
                        take(1),
                        tap((question) => this.addQuestion(question)),
                    );
                }),
            )
            .subscribe();

        effect(() => {
            const quiz = this.quiz();
            if (!quiz) return;

            this.form.patchValue({ name: quiz.name });
        });

        effect(() => {
            if (this.quizFacade.saved()) {
                this.close();
            }
        });

        effect(() => {
            if (this.quizFacade.saving() === false && this.quizFacade.saved() === false) {
                this.form.enable();
            }
        });

        effect(() => {
            const question = this.questionSelectionService.selected();
            if (!question) return;

            this.addQuestion(question);
            this.questionSelectionService.clear();
        });
    }

    ngOnDestroy(): void {
        this.quizFacade.clear();
    }

    onSubmit(): void {
        this.form.markAllAsTouched();
        if (this.form.invalid) return;

        const { name } = this.form.getRawValue();
        const questionIds = this.draftQuestions().map(({ id }) => id);
        this.quizFacade.updateQuiz(this.id, { name, questionIds });
    }

    onAddQuestion(): void {
        this.router.navigate([{ outlets: { dialog: ['add-question'] } }]);
    }

    onRemoveQuestion(index: number): void {
        const current = this.draftQuestions();
        this.draftQuestions.set(current.filter((_, i) => i !== index));
    }

    reorderQuestions(questions: QuizQuestion[]): void {
        this.draftQuestions.set(questions);
    }

    close(): void {
        this.router.navigate([{ outlets: { modal: null } }]);
    }

    private addQuestion(question: QuizQuestion): void {
        const current = this.draftQuestions();
        const alreadyExists = current.some(({ id }) => id === question.id);
        if (!alreadyExists) {
            this.draftQuestions.set([...current, question]);
        }
    }
}
